import { useState, useEffect } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, formatDate, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { FormField } from "@/pages/Contacts";
import { useChannels } from "@/hooks/useChannels";
import { KeyRound, Plus, Loader2, Copy, Check, RefreshCw, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AccessTokens() {
  const channels = useChannels();
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/access-tokens");
  const [scopeOptions, setScopeOptions] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState({ name: "", auth_type: "user", channel_id: "", scopes: [] });
  const [saving, setSaving] = useState(false);
  const [plaintext, setPlaintext] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/meta/token-scopes").then((r) => setScopeOptions(r.data || [])); }, []);
  const channelName = (id) => channels.find((c) => c.id === id)?.name || "-";

  const openCreate = () => { setForm({ name: "", auth_type: "user", channel_id: "", scopes: [] }); setPlaintext(null); setDrawer(true); };

  const toggleScope = (s) => setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));

  const create = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, auth_type: form.auth_type, scopes: form.scopes, channel_id: form.auth_type === "channel" ? form.channel_id : null };
      const res = await api.post("/access-tokens", payload);
      setPlaintext(res.data.plaintext_token);
      refetch();
      toast.success("Token berhasil dibuat.");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Cabut token "${t.name}"?`)) return;
    try { await api.post(`/access-tokens/${t.id}/revoke`, {}); toast.success("Token dicabut."); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const regenerate = async (t) => {
    if (!window.confirm(`Regenerate token "${t.name}"? Token lama akan dicabut.`)) return;
    try {
      const res = await api.post(`/access-tokens/${t.id}/regenerate`, {});
      setForm({ name: t.name, auth_type: t.auth_type, channel_id: t.channel_id || "", scopes: t.scopes });
      setPlaintext(res.data.plaintext_token);
      setDrawer(true);
      refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const copy = () => { navigator.clipboard.writeText(plaintext); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div>
      <PageHeader title="Access Token" subtitle="Token API Serabi untuk integrasi sistem eksternal Anda."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="create-token-button"><Plus className="w-4 h-4 mr-1.5" /> Generate Token</Button>} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari token..." testid="token-search" />
        <Select value={filters.auth_type || "all"} onValueChange={(v) => setFilters({ ...filters, auth_type: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="token-type-filter"><SelectValue placeholder="Tipe" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua tipe</SelectItem><SelectItem value="user">User</SelectItem><SelectItem value="channel">Channel</SelectItem></SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={KeyRound} title="Belum ada token" description="Generate token API untuk integrasi sistem eksternal Anda."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" /> Generate Token</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Nama</th>
                <th className={`${thClass} hidden md:table-cell`}>Prefix</th>
                <th className={`${thClass} hidden lg:table-cell`}>Tipe</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} hidden md:table-cell`}>Terakhir dipakai</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id} className={`${trClass} cursor-default`} data-testid={`token-row-${t.id}`}>
                  <td className={tdFirstClass}>
                    <p className="font-medium text-[#111111]">{t.name}</p>
                    {t.channel_id && <p className="text-xs text-[#767676]">{channelName(t.channel_id)}</p>}
                  </td>
                  <td className={`${tdClass} hidden md:table-cell font-mono text-[13px] text-[#003399]`}>{t.token_prefix}…</td>
                  <td className={`${tdClass} hidden lg:table-cell capitalize`}>{t.auth_type}</td>
                  <td className={tdClass}>
                    <StatusBadge status={t.is_active ? "active" : "revoked"} />
                  </td>
                  <td className={`${tdClass} hidden md:table-cell text-[#767676] text-xs`}>{t.last_used_at ? formatDate(t.last_used_at) : "Belum pernah"}</td>
                  <td className={`${tdClass} text-right whitespace-nowrap`}>
                    {t.is_active && <button onClick={() => regenerate(t)} className="text-[#767676] hover:text-[#003399] p-1.5 transition-colors" data-testid={`regenerate-token-${t.id}`} title="Regenerate"><RefreshCw className="w-4 h-4" /></button>}
                    {t.is_active && <button onClick={() => revoke(t)} className="text-[#767676] hover:text-[#CC0008] p-1.5 transition-colors" data-testid={`revoke-token-${t.id}`} title="Revoke"><Ban className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} title={plaintext ? "Token Anda" : "Generate Token"}
        description={plaintext ? "Simpan sekarang — hanya ditampilkan sekali!" : "Buat token API Serabi baru."} testid="token-drawer"
        footer={!plaintext ? <>
          <Button variant="outline" onClick={() => setDrawer(false)}>Batal</Button>
          <Button onClick={create} disabled={saving || !form.name || form.scopes.length === 0} className="bg-[#003399] hover:bg-[#002B80]" data-testid="token-generate-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
          </Button>
        </> : <Button onClick={() => setDrawer(false)} className="bg-[#003399] hover:bg-[#002B80]" data-testid="token-done">Selesai</Button>}>
        {plaintext ? (
          <div className="space-y-4">
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
              Token ini hanya ditampilkan sekali. Salin dan simpan di tempat aman.
            </div>
            <div className="rounded-lg border border-neutral-300 bg-neutral-900 p-4 flex items-center gap-2">
              <code className="font-mono text-xs text-emerald-300 break-all flex-1" data-testid="token-plaintext">{plaintext}</code>
              <button onClick={copy} className="text-neutral-400 hover:text-white p-1.5 flex-shrink-0" data-testid="copy-token">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <FormField label="Nama Token *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Production CRM Token" testid="token-form-name" />
            <div className="space-y-2">
              <Label>Tipe Autentikasi *</Label>
              <Select value={form.auth_type} onValueChange={(v) => setForm({ ...form, auth_type: v })}>
                <SelectTrigger data-testid="token-form-authtype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="user">User (workspace)</SelectItem><SelectItem value="channel">Channel</SelectItem></SelectContent>
              </Select>
            </div>
            {form.auth_type === "channel" && (
              <div className="space-y-2">
                <Label>Channel *</Label>
                <Select value={form.channel_id} onValueChange={(v) => setForm({ ...form, channel_id: v })}>
                  <SelectTrigger data-testid="token-form-channel"><SelectValue placeholder="Pilih channel" /></SelectTrigger>
                  <SelectContent>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Scopes *</Label>
              <div className="grid grid-cols-2 gap-2">
                {scopeOptions.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 text-sm p-2 rounded-[4px] bg-[#F5F5F5] hover:bg-[#ECECEC] cursor-pointer" data-testid={`scope-${s.value}`}>
                    <Checkbox checked={form.scopes.includes(s.value)} onCheckedChange={() => toggleScope(s.value)} />
                    <span className="font-mono text-xs">{s.value}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}
